import { Component, EventEmitter, Inject, Input, OnInit, Optional, Output } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ContactBankAccountModel } from './contactbankaccount.model';
import { ContactBankAccountService } from './contactbankaccount.service';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';

export interface ContactBankAccountFormDialogData {
  uid: string;
  bankAccountUid?: string;
}

@Component({
    selector: 'convfact-contactbankaccount-form',
    templateUrl: './contactbankaccount-form.component.html',
    styleUrls: ['./contactbankaccount.component.css'],
    standalone: false
})
export class ContactBankAccountFormComponent implements OnInit {
  @Input() contactUid: string;
  @Input() bankAccountUid: string;
  @Output() saved = new EventEmitter<ContactBankAccountModel>();
  @Output() cancelled = new EventEmitter<void>();

  contactBankAccount: ContactBankAccountModel = new ContactBankAccountModel();
  ready = false;
  saving = false;
  submitted = false;

  static open(dialog: MatDialog, data: ContactBankAccountFormDialogData): Observable<boolean> {
    return dialog.open<ContactBankAccountFormComponent, ContactBankAccountFormDialogData, boolean>(ContactBankAccountFormComponent, {
      width: '560px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private contactBankAccountService: ContactBankAccountService,
    private notify: NotificationService,
    @Optional() public dialogRef: MatDialogRef<ContactBankAccountFormComponent, boolean>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: ContactBankAccountFormDialogData,
  ) {
    if (data) {
      this.contactUid = data.uid;
      this.bankAccountUid = data.bankAccountUid;
    }
  }

  get isEdit(): boolean {
    return !!this.bankAccountUid;
  }

  get missingIban(): boolean {
    return !this.clean(this.contactBankAccount.iban);
  }

  get invalidIban(): boolean {
    return !this.missingIban && !this.isIban(this.contactBankAccount.iban);
  }

  get missingBank(): boolean {
    return !this.clean(this.contactBankAccount.bank);
  }

  get invalidSwift(): boolean {
    const swift = this.clean(this.contactBankAccount.swift);
    return !!swift && !this.isBic(swift);
  }

  ngOnInit(): void {
    if (!this.isEdit) {
      this.ready = true;
      return;
    }
    this.contactBankAccountService.loadData(this.contactUid, this.bankAccountUid)
      .then(bankAccount => {
        if (!bankAccount) throw new Error();
        this.contactBankAccount = bankAccount;
        this.ready = true;
      })
      .catch(error => {
        this.notify.error(error?.error?.message || 'No se pudo cargar la cuenta bancaria');
        this.cancel();
      });
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    if (this.missingIban || this.invalidIban || this.missingBank || this.invalidSwift) return;

    const payload: ContactBankAccountModel = {
      ...this.contactBankAccount,
      iban: this.clean(this.contactBankAccount.iban),
      bank: this.clean(this.contactBankAccount.bank),
      swift: this.clean(this.contactBankAccount.swift).toUpperCase(),
      remesa: !!this.contactBankAccount.remesa,
    };

    this.saving = true;
    if (this.dialogRef) this.dialogRef.disableClose = true;

    const request = this.isEdit
      ? this.contactBankAccountService.editData(this.contactUid, this.bankAccountUid, payload)
      : this.contactBankAccountService.addData(this.contactUid, payload);

    request
      .then(result => {
        this.notify.ok(this.isEdit ? 'Cuenta bancaria actualizada' : 'Cuenta bancaria creada');
        if (this.dialogRef) {
          this.dialogRef.disableClose = false;
          this.dialogRef.close(true);
          return;
        }
        this.saving = false;
        this.saved.emit(result);
      })
      .catch(error => {
        this.saving = false;
        if (this.dialogRef) this.dialogRef.disableClose = false;
        this.notify.error(error?.error?.message || 'No se pudo guardar la cuenta bancaria');
      });
  }

  cancel(): void {
    if (this.saving) return;
    if (this.dialogRef) {
      this.dialogRef.close(false);
      return;
    }
    this.cancelled.emit();
  }

  private clean(value: string): string {
    return (value || '').trim();
  }

  private isBic(value: string): boolean {
    return /^([A-Z]{6}[A-Z2-9][A-NP-Z1-9])(X{3}|[A-WY-Z0-9][A-Z0-9]{2})?$/.test(value.toUpperCase());
  }

  private isIban(value: string): boolean {
    const iban = (value || '').replace(/\s+/g, '').toUpperCase();
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(iban)) return false;
    const rearranged = iban.substring(4) + iban.substring(0, 4);
    let remainder = 0;
    for (const char of rearranged) {
      const digits = /[A-Z]/.test(char) ? (char.charCodeAt(0) - 55).toString() : char;
      for (const digit of digits) {
        remainder = (remainder * 10 + Number(digit)) % 97;
      }
    }
    return remainder === 1;
  }
}
